// Third-party libraries
import React, { useContext, useState, useEffect } from "react";
import {
  Select,
  Typography,
  Grid,
  Stack,
  TextField,
  MenuItem,
  FormControl,
  FormHelperText,
} from "@mui/material";
import Creatable from "react-select/creatable";

// Local modules
import { BandsContext } from "../../../../utils/stateHandlers/contexts";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { constants } from "../../../../utils";
import Modal from "../../../diamonds/controls/Modal";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export default function BandsFields(props) {
  const { bandsState, bandsDispatch } = useContext(BandsContext);
  const { clear, validationErrors, priceLoaded, productInfo } = bandsState;
  const [localSelection, setLocalSelection] = useState(null);
  const [localInput, setLocalInput] = useState(props.field.default);
  const [localUnit, setLocalUnit] = useState(null);
  const fieldType = props.field.template;
  const bandCount = props.bandCount;
  const handleInputChange = (e) => {
    setLocalInput(e.target.value);
  };
  const [showHelp, setShowHelp] = useState(false);
  const [helpTitle, setHelpTitle] = useState("");
  const [helpText, setHelpText] = useState("");
  const [helpImage, setHelpImage] = useState(null);
  const frontImageUrl = constants.CMS_FRONT_IMAGE_URL;

  function updateValueLabelInPlace(data, targetFieldLabel, oldLabel, newLabel) {
    if (
      data.label === targetFieldLabel &&
      data.values &&
      Array.isArray(data.values)
    ) {
      data.values.forEach((item) => {
        if (item.label === oldLabel) {
          item.label = newLabel;
        }
      });
    }
  }
  updateValueLabelInPlace(props.field, "Stone Provided By", "NONE", "UNSET");

  useEffect(() => {
    if (props.field.name === "complex[1][1][1][quantity]") {
      bandsDispatch({
        type: "updateFields",
        field: "complex[1][1][1][quantity]",
        payload: localInput,
      });
    }
    if (props.field.name === "complex[1][1][1][quality]") {
      bandsDispatch({
        type: "updateFields",
        field: "complex[1][1][1][quality]",
        payload: localInput,
      });
    }
    if (localInput !== props.field.default) {
      const timeoutId = setTimeout(
        () =>
          bandsDispatch({
            type: "updateFields",
            field: props.field.name,
            payload: localInput,
          }),
        1000
      );
      return () => clearTimeout(timeoutId);
    }
  }, [localInput]);

  function selectValue(value, field_name) {
    if (
      field_name === "complex[finger_size]" ||
      field_name === "complex[1][band][width]" ||
      field_name === "complex[2][band][width]" ||
      field_name === "complex[3][band][width]"
    ) {
      if (value === "None") {
        bandsDispatch({
          type: "setValidationErrors",
          payload: [...validationErrors, field_name],
        });
      } else {
        const remove = validationErrors.filter(function (error) {
          return error !== "field_name";
        });
        bandsDispatch({ type: "setValidationErrors", payload: remove });
      }
      bandsDispatch({ type: "setClear", payload: false });
      setLocalSelection(value);
      bandsDispatch({
        type: "updateFields",
        field: field_name,
        payload: value,
      });
    } else {
      bandsDispatch({ type: "setClear", payload: false });
      setLocalSelection(value);
      bandsDispatch({
        type: "updateFields",
        field: field_name,
        payload: value,
      });
    }
  }

  function defaultSelectValue(field) {
    if (field.default) {
      const defaultValue = field.values.find(function (v) {
        return v.value === field.default;
      });
      return defaultValue;
    } else {
      return field.values[0];
    }
  }

  function materialOptionsFormatting(options) {
    const standardOptions = [];
    const mtoOptions = [];
    const fairminedStandardOptions = [];
    const fairminedMtoOptions = [];

    options.forEach(function (option) {
      return option.category === "Standard" || option.category === "S"
        ? standardOptions.push(option)
        : option.category === "MTO" || option.category === "M"
        ? mtoOptions.push(option)
        : option.category === "Fairmined - Standard" || option.category === "F"
        ? fairminedStandardOptions.push(option)
        : option.category === "Fairmined - MTO" || option.category === "G"
        ? fairminedMtoOptions.push(option)
        : "";
    });

    return [
      { label: "Standard", options: standardOptions },
      { label: "Made To Order", options: mtoOptions },
      { label: "Fairmined - Standard", options: fairminedStandardOptions },
      { label: "Fairmined - Made To Order", options: fairminedMtoOptions },
    ];
  }

  function multiBandAssigning(value, fieldType, bandCount) {
    for (var i = 1; i - 1 < bandCount; i++) {
      var fieldName = `complex[${i}][band]${fieldType}`;
      selectValue(value, fieldName);
    }
  }

  const SelectField = (field) => {
    // Determine condition flags cleanly inside the function scope
    const isStandardMaterial =
      field.name === "complex[1][band][material]" ||
      field.name === "complex[2][band][material]" ||
      field.name === "complex[3][band][material]" ||
      field.name === "material";

    const isMultiMaterial = field.name === "complex[multiMaterial]";

    return (
      <Stack key={field.name}>
        <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
          {field.label}
          {props.bubbles && (
            <InfoIcon
              sx={{ marginLeft: "0.5rem" }}
              className="pointer"
              color="primary"
              onClick={() => {
                setShowHelp(true);
                setHelpTitle(props.bubbles.bubbletitle);
                setHelpText(props.bubbles.bubbletext);
                setHelpImage(
                  props.bubbles.bubbleimage &&
                    props.bubbles.bubbleimage.data !== null
                    ? props.bubbles.bubbleimage.data.attributes
                    : null
                );
              }}
            />
          )}
        </Typography>
        {field.values && field.values.length > 0 ? (
          <FormControl error={localSelection === "None"}>
            {isStandardMaterial ? (
              <>
                <Creatable
                  key={field.name}
                  defaultValue={defaultSelectValue(field)}
                  isDisabled={!priceLoaded}
                  onChange={(e) => {
                    selectValue(e.value, field.name);
                  }}
                  options={materialOptionsFormatting(field.values)}
                  formatGroupLabel={(data) => (
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: "bold",
                        color: "#333",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {data.label}
                    </div>
                  )}
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                    colors: {
                      ...theme.colors,
                      primary25: "#cfdde9",
                      primary: "#124760",
                    },
                  })}
                />
              </>
            ) : (
              <>
                <Select
                  key={field.name}
                  value={
                    localSelection !== null
                      ? localSelection
                      : field.default !== "" && field.default
                      ? field.default
                      : "None"
                  }
                  size="small"
                  sx={{ width: "100%" }}
                  disabled={!priceLoaded || props.locked}
                  onChange={(e) => {
                    if (bandCount > 1) {
                      const match = field.name.match(/\[[^\[\]]*\]$/);
                      const fieldType = match ? match[0] : null;

                      switch (fieldType) {
                        case "[rhodium]]":
                        case "[fitType]":
                          multiBandAssigning(
                            e.target.value,
                            fieldType,
                            bandCount
                          );
                          break;
                        default:
                      }
                    }
                    if (isMultiMaterial) {
                      const fullValue = e.target.value;
                      const chunkLength = 3;

                      // Loop through the string in steps of 3
                      // We use a counter 'i' starting at 1 for your fieldName indexing
                      for (
                        var i = 1;
                        (i - 1) * chunkLength < fullValue.length;
                        i++
                      ) {
                        var startIndex = (i - 1) * chunkLength;
                        var chunk = fullValue.substring(
                          startIndex,
                          startIndex + chunkLength
                        );

                        var fieldName = "complex[" + i + "][band][material]";
                        selectValue(chunk, fieldName);
                      }
                      selectValue(fullValue, field.name);
                    } else {
                      handleInputChange(e);
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        border: "1px solid #939598",
                        borderRadius: "0",
                        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                      },
                    },
                  }}
                >
                  <MenuItem value="None" disabled>
                    <em>Select...</em>
                  </MenuItem>
                  {field.values.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={option.value === "Call"}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            <FormHelperText>
              {localSelection === "None" ? "Please Make A Selection" : ""}
            </FormHelperText>
          </FormControl>
        ) : (
          <Typography sx={{ fontStyle: "italic" }}>
            No options available for selection.
          </Typography>
        )}
      </Stack>
    );
  };

  const QuantityField = (field) => (
    <Grid container key={field.name}>
      <Grid item xs={8}>
        <Stack>
          <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
            Quantity
          </Typography>
          <TextField
            color="primary"
            disabled={!priceLoaded || props.locked}
            onChange={(e) => {
              handleInputChange(e);
            }}
            type="number"
            size="small"
            sx={{ width: "100%", margin: "unset" }}
            value={localInput}
          />
        </Stack>
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={3}>
        <Stack>
          <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
            Units
          </Typography>
          <Select
            value={localUnit === null ? "EA" : localUnit}
            sx={{ width: "100%" }}
            size="small"
            disabled={!priceLoaded || (field.units && field.units.length === 1)}
            onChange={(e) => {
              bandsDispatch({
                type: "updateFields",
                field: "builder[quantity_unit]",
                payload: e.target.value,
              });
              setLocalUnit(e.target.value);
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1px solid #939598",
                  borderRadius: "0",
                  maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                },
              },
            }}
          >
            <MenuItem value="None" disabled>
              <em>Select...</em>
            </MenuItem>
            {field.units &&
              field.units.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </Stack>
      </Grid>
    </Grid>
  );

  const InputSelectField = (field) => (
    <Grid container key={field.name}>
      <Grid item xs={8}>
        <Stack>
          <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
            {field.label}
          </Typography>
          <TextField
            color="primary"
            disabled={!priceLoaded}
            onChange={(e) => {
              handleInputChange(e);
            }}
            type="number"
            size="small"
            sx={{ width: "100%", margin: "unset" }}
            value={localInput}
          />
        </Stack>
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={3}>
        <Stack>
          <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
            Units
          </Typography>
          <Select
            value={localUnit === null ? field.default_unit : localUnit}
            sx={{ width: "100%" }}
            size="small"
            disabled={!priceLoaded}
            onChange={(e) => {
              bandsDispatch({
                type: "updateFields",
                field: field.name,
                payload: e.target.value,
              });
              setLocalUnit(e.target.value);
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1px solid #939598",
                  borderRadius: "0",
                  maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                },
              },
            }}
          >
            <MenuItem value="None" disabled>
              <em>Select...</em>
            </MenuItem>
            {field.units &&
              field.units.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </Stack>
      </Grid>
    </Grid>
  );

  const field =
    props.field.name === "builder[quantity]"
      ? QuantityField(props.field)
      : fieldType === "dropdown"
      ? SelectField(props.field)
      : fieldType === "field_with_units"
      ? InputSelectField(props.field)
      : "";

  useEffect(() => {
    if (clear) {
      bandsDispatch({ type: "setClear", payload: false });
      bandsDispatch({
        type: "setValidationErrors",
        payload: [...validationErrors, props.field.name],
      });
      setLocalSelection("None");
      bandsDispatch({
        type: "updateFields",
        field: props.field.name,
        payload: "None",
      });
    }
  }, [clear]);

  return (
    <>
      {field}
      <Modal
        title={helpTitle}
        onClose={() => setShowHelp(false)}
        show={showHelp}
      >
        {helpImage !== null && (
          <img
            src={`${frontImageUrl}${helpImage.url}`}
            width="100%"
            alt="helpImage"
          />
        )}
        <Typography variant="staticText">
          <ReactMarkdown children={helpText} remarkPlugins={[remarkGfm]} />
        </Typography>
      </Modal>
    </>
  );
}
